import React, { useState } from 'react';
import { Code, ArrowUpDown, Copy, Check } from 'lucide-react';

export default function Home() {
    const [inputCode, setInputCode] = useState('');
    const [outputCode, setOutputCode] = useState('');
    const [mode, setMode] = useState('comment'); // 'comment' or 'uncomment'
    const [copied, setCopied] = useState(false);

    const convertToMegaComment = (code) => {
        if (!code.trim()) return '';

        // Replace {/* with @@->@@
        let converted = code.replace(/\{\s*\/\*/g, '@@->@@');
        // Replace */} with @@||@@
        converted = converted.replace(/\*\/\s*\}/g, '@@||@@');

        // Wrap the entire converted code in a mega comment
        return `{/* ${converted} */}`;
    };

    const convertFromMegaComment = (code) => {
        if (!code.trim()) return '';

        // Remove outer mega comment wrapper
        let converted = code.replace(/^\{\s*\/\*\s*/, '').replace(/\s*\*\/\s*\}\s*$/, '');

        // Convert @@->@@ back to {/*
        converted = converted.replace(/@@->@@/g, '{/*');
        // Convert @@||@@ back to */}
        converted = converted.replace(/@@\|\|@@/g, '*/}');

        return converted;
    };

    const handleConvert = () => {
        if (mode === 'comment') {
            setOutputCode(convertToMegaComment(inputCode));
        } else {
            setOutputCode(convertFromMegaComment(inputCode));
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(outputCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const toggleMode = () => {
        setMode(mode === 'comment' ? 'uncomment' : 'comment');
        setInputCode('');
        setOutputCode('');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Code className="w-8 h-8 text-indigo-600" />
                        <h1 className="text-4xl font-bold text-gray-800">Comment Toggle</h1>
                    </div>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        <b>Comment Out and Uncomment Multi Line JSX Code with Comments with ease. </b><br />
                        Convert JSX code with existing comments into mega-comments and back.
                        Perfect for temporarily commenting out code blocks without losing original comments.
                    </p>
                </div>

                {/* Mode Toggle */}
                <div className="flex justify-center mb-6">
                    <button
                        onClick={toggleMode}
                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
                    >
                        <ArrowUpDown className="w-5 h-5" />
                        Switch to {mode === 'comment' ? 'Uncomment' : 'Comment'} Mode
                    </button>
                </div>

                {/* Current Mode Indicator */}
                <div className="text-center mb-6">
                    <span className="inline-block px-4 py-2 bg-white rounded-full shadow-md text-sm font-medium">
                        Current Mode: <span className="text-indigo-600 font-bold">
                            {mode === 'comment' ? 'Convert to Mega-Comment' : 'Convert from Mega-Comment'}
                        </span>
                    </span>
                </div>

                {/* Main Content */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Input Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">
                            {mode === 'comment' ? 'JSX Code with Comments' : 'Mega-Comment Code'}
                        </h2>
                        <textarea
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            placeholder={mode === 'comment'
                                ? 'Paste your JSX code here...\n\nExample:\n<div className="container">\n  {/* This is a comment */}\n  <p>Hello World</p>\n</div>'
                                : 'Paste your mega-comment code here...\n\nExample:\n{/* <div className="container">\n  @@@{@@@ This is a comment @@@}@@@\n  <p>Hello World</p>\n</div> */}'
                            }
                            className="w-full h-64 p-4 border border-gray-200 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <button
                            onClick={handleConvert}
                            disabled={!inputCode.trim()}
                            className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {mode === 'comment' ? 'Convert to Mega-Comment' : 'Convert to Normal Code'}
                        </button>
                    </div>

                    {/* Output Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {mode === 'comment' ? 'Mega-Comment Output' : 'Normal Code Output'}
                            </h2>
                            {outputCode && (
                                <button
                                    onClick={handleCopy}
                                    className="flex items-center gap-2 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm"
                                >
                                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            )}
                        </div>
                        <textarea
                            value={outputCode}
                            readOnly
                            placeholder="Converted code will appear here..."
                            className="w-full h-64 p-4 border border-gray-200 rounded-lg font-mono text-sm resize-none bg-gray-50"
                        />
                    </div>
                </div>

                {/* How it Works */}
                <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">How It Works</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-medium text-indigo-600 mb-2">Converting to Mega-Comment</h3>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>
                                    1. Replaces <code className="bg-gray-100 px-1 rounded">{'{/*'}</code> with <code className="bg-gray-100 px-1 rounded">@@-&gt;@@</code>
                                </p>
                                <p>
                                    2. Replaces <code className="bg-gray-100 px-1 rounded">{'*/}'}</code> with <code className="bg-gray-100 px-1 rounded">@@||@@</code>
                                </p>
                                <p>
                                    3. Wraps entire code in <code className="bg-gray-100 px-1 rounded">{'{/* ... */}'}</code>
                                </p>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-medium text-indigo-600 mb-2">Converting from Mega-Comment</h3>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>1. Removes outer mega-comment wrapper</p>
                                <p>
                                    2. Converts <code className="bg-gray-100 px-1 rounded">@@-&gt;@@</code> back to <code className="bg-gray-100 px-1 rounded">{'{/*'}</code>
                                </p>
                                <p>
                                    3. Converts <code className="bg-gray-100 px-1 rounded">@@||@@</code> back to <code className="bg-gray-100 px-1 rounded">{'*/}'}</code>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Example */}
                <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Example</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <h4 className="font-medium text-gray-700 mb-2">Original Code:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-sm overflow-x-auto">
                                {`<div className="container">
  {/* This is a comment */}
  <p>Hello World</p>
  {/* Another comment */}
</div>`}
                            </pre>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-2">Mega-Comment:</h4>
                            <pre className="bg-gray-50 p-3 rounded text-sm overflow-x-auto">
                                {`{/* <div className="container">
  @@->@@ This is a comment @@||@@
  <p>Hello World</p>
  @@->@@ Another comment @@||@@
</div> */}`}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}